import { defineStore } from "pinia";

/**
 * Globalny store powiadomień (toastów).
 *
 * Jedno miejsce, przez które cała aplikacja zgłasza wynik operacji — zamiast
 * lokalnych bloków `.alert` rozsianych po widokach i komponentach. Renderowaniem
 * zajmuje się `components/ui/AppNotifications.vue`, zamontowany raz w `CarViver.vue`,
 * dzięki czemu komunikat przeżywa zmianę trasy (np. „hasło zmienione" pokazane
 * po przekierowaniu na /login).
 *
 * Użycie:
 *   const notify = useNotificationStore();
 *   notify.success(t('password-changed'));
 *   notify.error(err);                      // przyjmuje Error/obiekt/string/status
 */

// Czas życia powiadomienia (ms) w zależności od wagi. Błąd trzyma się dłużej —
// użytkownik musi go przeczytać i zwykle powtórzyć operację; sukces to tylko
// potwierdzenie, że akcja przeszła.
const DEFAULT_DURATION = {
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 8000,
};

// Ile toastów pokazujemy jednocześnie. Powyżej tego limitu najstarszy znika —
// przy pollingu (bieżące pomiary) seria błędów potrafiłaby zalać cały ekran.
const MAX_VISIBLE = 4;

// Uchwyty setTimeout trzymamy POZA stanem Pinia: to wartości nieserializowalne,
// a ich podmiana nie powinna wywoływać przeliczeń reaktywnych.
const timers = new Map();

let nextId = 1;

function clearTimer(id) {
  const handle = timers.get(id);
  if (handle) {
    clearTimeout(handle);
    timers.delete(id);
  }
}

/**
 * Nazwa pola, którą `fetch-wrapper` skleja z komunikatem backendu:
 * `data[0].path + ": " + data[0].userMessage`, np.
 * "newPassword: Hasło musi zawierać przynajmniej jedną cyfrę.".
 *
 * Dla użytkownika ta nazwa jest szumem — pole i tak jest podświetlone w formularzu.
 * Ścinamy więc prefiks, ale tylko wtedy, gdy naprawdę wygląda jak ścieżka pola:
 * zaczyna się MAŁĄ literą i nie ma spacji (dopuszczamy zagnieżdżenia i indeksy,
 * np. `lanes[0].laneNumber`). Dzięki temu zdanie zaczynające się od wielkiej litery
 * ("Uwaga: ...") zostaje nietknięte.
 */
const FIELD_PATH_PREFIX =
  /^[a-z][A-Za-z0-9_]*(\[\d+\])?(\.[A-Za-z0-9_]+(\[\d+\])?)*\s*:\s*/;

/**
 * Sprowadza dowolny „powód niepowodzenia" do czytelnego tekstu.
 * `fetch-wrapper` odrzuca promisy stringiem "pole: komunikat" albo gołym
 * numerem statusu HTTP, serwisy potrafią rzucić Error, a część kodu przekazuje
 * obiekt odpowiedzi — obsługujemy wszystkie te warianty.
 */
function normalizeMessage(input) {
  if (input == null) return "";
  if (input instanceof Error) return stripFieldPath(input.message || String(input));
  if (typeof input === "number") return String(input);
  if (typeof input === "object") {
    const fromField = input.message || input.error || input.userMessage || input.msg;
    if (typeof fromField === "string") return stripFieldPath(fromField);
    try {
      return JSON.stringify(input);
    } catch {
      return String(input);
    }
  }
  return stripFieldPath(String(input));
}

/**
 * Zdejmuje z komunikatu prefiks z nazwą pola (patrz `FIELD_PATH_PREFIX`) oraz
 * pozostałość po nieustawionej ścieżce ("null: ..."), którą potrafi zwrócić API.
 */
export function stripFieldPath(text) {
  if (typeof text !== "string") return "";
  const withoutNull = text.replace(/^null\s*:\s*/i, "");
  const stripped = withoutNull.replace(FIELD_PATH_PREFIX, "");
  // Gdyby po ścięciu nie zostało nic sensownego, lepiej pokazać oryginał
  // niż pusty toast.
  return stripped.trim() === "" ? withoutNull.trim() : stripped.trim();
}

export const useNotificationStore = defineStore({
  id: "notifications",

  state: () => ({
    /**
     * Lista widocznych powiadomień. Każdy wpis:
     *  id        – identyfikator (klucz listy i timera),
     *  type      – 'success' | 'error' | 'warning' | 'info',
     *  message   – treść,
     *  duration  – pełny czas życia w ms (0 = bez auto-zamykania),
     *  remaining – ile ms zostało (maleje przy pauzie na hover),
     *  paused    – czy odliczanie jest wstrzymane,
     *  count     – ile razy ten sam komunikat wpadł pod rząd,
     *  runId     – zmienia się przy restarcie odliczania; wymusza ponowne
     *              odtworzenie animacji paska postępu.
     */
    items: [],
  }),

  actions: {
    /**
     * Dodaje powiadomienie. Gdy identyczny komunikat już wisi na ekranie,
     * nie dokładamy kolejnego kafelka — restartujemy odliczanie i zwiększamy
     * licznik powtórzeń (typowe przy cyklicznym odpytywaniu API).
     */
    push(message, type = "info", options = {}) {
      const text = normalizeMessage(message);
      if (!text) return null;

      const duration = options.duration ?? DEFAULT_DURATION[type] ?? DEFAULT_DURATION.info;

      const existing = this.items.find(
        (item) => item.type === type && item.message === text
      );
      if (existing) {
        existing.count += 1;
        this._restart(existing);
        return existing.id;
      }

      const item = {
        id: nextId++,
        type,
        message: text,
        duration,
        remaining: duration,
        paused: false,
        count: 1,
        runId: 0,
        startedAt: 0,
      };
      this.items.push(item);

      // Limit widocznych — najstarsze wypadają pierwsze.
      while (this.items.length > MAX_VISIBLE) {
        this.dismiss(this.items[0].id);
      }

      this._startTimer(item);
      return item.id;
    },

    success(message, options = {}) {
      return this.push(message, "success", options);
    },

    error(message, options = {}) {
      return this.push(message, "error", options);
    },

    warning(message, options = {}) {
      return this.push(message, "warning", options);
    },

    info(message, options = {}) {
      return this.push(message, "info", options);
    },

    dismiss(id) {
      clearTimer(id);
      const index = this.items.findIndex((item) => item.id === id);
      if (index !== -1) this.items.splice(index, 1);
    },

    clear() {
      this.items.forEach((item) => clearTimer(item.id));
      this.items = [];
    },

    /**
     * Wstrzymuje odliczanie (kursor nad powiadomieniem) i zapamiętuje, ile
     * czasu zostało — pasek postępu zatrzymuje się w tym samym miejscu.
     */
    pause(id) {
      const item = this.items.find((entry) => entry.id === id);
      if (!item || item.paused || !item.duration) return;
      clearTimer(id);
      item.remaining = Math.max(0, item.remaining - (Date.now() - item.startedAt));
      item.paused = true;
    },

    resume(id) {
      const item = this.items.find((entry) => entry.id === id);
      if (!item || !item.paused) return;
      item.paused = false;
      this._startTimer(item);
    },

    _startTimer(item) {
      clearTimer(item.id);
      if (!item.duration) return; // 0 = powiadomienie „przyklejone", zamykane ręcznie
      item.startedAt = Date.now();
      timers.set(
        item.id,
        setTimeout(() => this.dismiss(item.id), item.remaining)
      );
    },

    _restart(item) {
      item.remaining = item.duration;
      item.paused = false;
      item.runId += 1; // wymusza restart animacji paska w komponencie
      this._startTimer(item);
    },
  },
});

export { normalizeMessage };
